import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Input, Popconfirm, Spin, Tag, Typography } from "antd";
import type { ButtonProps } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { createNoteTag, deleteNoteTag, getNoteTags } from "@store/actions/notes.actions";
import type { NoteTag, NotesState } from "@store/reducers/notes.reducer";
import { readNoteApiError } from "@utils/noteApiError.mjs";
import { sortAttributesByName, usageText } from "@utils/noteAttributes.mjs";
import { labelError, normalizeLabel } from "@utils/notesForm.mjs";

const TAGS_DESCRIPTION = "Tags are shared with your whole team. A tag can be deleted only when no note uses it.";

const DELETE_CONFIRM_PROPS = { danger: true, "data-attr": "notes.tags.confirm" } as ButtonProps;
const CANCEL_PROPS = { "data-attr": "notes.tags.cancel" } as ButtonProps;

export const TagSettings = () => {
    const dispatch = useDispatch<any>();
    const tags = useSelector((state: { notes: NotesState }) => state.notes.tags);
    const loading = useSelector((state: { notes: NotesState }) => state.notes.loading.tags);

    const [creating, setCreating] = useState(false);
    const [name, setName] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // The usage counts must be fresh. The thunk shows its own errors. The `catch` only stops an unhandled rejection.
    useEffect(() => {
        dispatch(getNoteTags()).catch(() => {});
    }, [dispatch]);

    const sorted = useMemo(() => sortAttributesByName(tags), [tags]);

    const closeCreate = () => {
        setCreating(false);
        setName("");
        setError(null);
    };

    const handleCreate = async () => {
        const clientError = labelError(name);
        setError(clientError);
        if (clientError) return;
        setSaving(true);
        try {
            await dispatch(createNoteTag(normalizeLabel(name)));
            closeCreate();
            message.success("Tag created.");
        } catch (e) {
            // The thunk shows an error that has no field. An error with a field shows under the input.
            const { message: text, field } = readNoteApiError(e);
            if (field !== null) setError(text);
        } finally {
            setSaving(false);
        }
    };

    // The thunk shows the error, such as a tag that a note started to use. The `catch` only stops an unhandled rejection.
    const remove = (tag: NoteTag) => dispatch(deleteNoteTag(tag.id)).catch(() => {});

    // A tag that a note uses cannot be deleted, so only an unused tag shows the delete icon.
    const renderTag = (tag: NoteTag) => (
        <Tag key={tag.id} bordered={false} color="blue" className="notes-tag-chip" title={usageText(tag.usage_count)}>
            {tag.name}
            <span className="notes-tag-chip-count">{tag.usage_count}</span>
            {tag.usage_count === 0 && (
                <Popconfirm
                    title={`Delete “${tag.name}”?`}
                    okText="Delete"
                    okButtonProps={DELETE_CONFIRM_PROPS}
                    cancelButtonProps={CANCEL_PROPS}
                    onConfirm={() => remove(tag)}
                >
                    <Button
                        type="text"
                        size="small"
                        className="notes-tag-chip-delete"
                        icon={<DeleteOutlined />}
                        aria-label={`Delete ${tag.name}`}
                        title="Delete tag"
                        data-attr="notes.tags.delete"
                    />
                </Popconfirm>
            )}
        </Tag>
    );

    return (
        <Card
            title="Note tags"
            extra={
                <Button disabled={creating} onClick={() => setCreating(true)} data-attr="notes.tags.open-create">
                    + New tag
                </Button>
            }
        >
            <Typography.Paragraph type="secondary">{TAGS_DESCRIPTION}</Typography.Paragraph>
            {creating && (
                <div className="notes-dialog-create-attribute">
                    <div className="notes-dialog-create-row">
                        <div className="notes-dialog-create-name">
                            <label className="notes-filter-label" htmlFor="notes-tag-name">
                                Name
                            </label>
                            <Input
                                id="notes-tag-name"
                                autoFocus
                                placeholder="e.g. Injury"
                                status={error ? "error" : undefined}
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    setError(null);
                                }}
                                onPressEnter={handleCreate}
                            />
                        </div>
                        <Button type="primary" loading={saving} onClick={handleCreate} data-attr="notes.tags.create">
                            Create
                        </Button>
                        <Button disabled={saving} onClick={closeCreate} data-attr="notes.tags.cancel-create">
                            Cancel
                        </Button>
                    </div>
                    {error && <Typography.Text type="danger">{error}</Typography.Text>}
                </div>
            )}
            <Spin spinning={loading}>
                {sorted.length > 0 ? (
                    <div className="notes-tag-list">{sorted.map(renderTag)}</div>
                ) : (
                    <Typography.Text type="secondary">No tags yet.</Typography.Text>
                )}
            </Spin>
        </Card>
    );
};
